import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2 } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const TEAM_ROLES = ["Broker Owner", "Broker Admin", "Broker Agent", "Dispatcher", "Compliance", "Accounting", "Read Only"];

export const BrokerTeamTab = () => {
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ member_name: "", member_email: "", role: TEAM_ROLES[2] });
  const [adding, setAdding] = useState(false);

  const load = () => brokerApi.get("/team").then(({ data }) => setTeam(data)).catch(() => setTeam([]));
  useEffect(() => { load(); }, []);

  const addMember = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await brokerApi.post("/team", form);
      toast.success(`${form.member_name} added to your team.`);
      setForm({ member_name: "", member_email: "", role: TEAM_ROLES[2] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add team member.");
    } finally {
      setAdding(false);
    }
  };

  const removeMember = async (id) => {
    try {
      await brokerApi.del(`/team/${id}`);
      toast.success("Removed from team.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove team member.");
    }
  };

  if (!team) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="broker-team-tab">
      <h2 className="font-heading text-xl font-bold text-rr-navy mb-5">Team</h2>
      <form onSubmit={addMember} data-testid="broker-add-team-member-form" className="bg-white border border-rr-line rounded-2xl p-6 max-w-xl grid sm:grid-cols-3 gap-3 mb-6">
        <input
          required
          data-testid="add-team-member-name-input"
          placeholder="Name"
          value={form.member_name}
          onChange={(e) => setForm((f) => ({ ...f, member_name: e.target.value }))}
          className="border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
        />
        <input
          required
          type="email"
          data-testid="add-team-member-email-input"
          placeholder="Email"
          value={form.member_email}
          onChange={(e) => setForm((f) => ({ ...f, member_email: e.target.value }))}
          className="border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
        />
        <select
          data-testid="add-team-member-role-select"
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
          className="border border-rr-line rounded-lg px-3 py-3 text-sm bg-white focus:outline-none focus:border-rr-orange"
        >
          {TEAM_ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
        <button type="submit" disabled={adding} data-testid="add-team-member-submit-button" className="sm:col-span-3 bg-rr-orange text-white font-bold px-6 py-3 rounded-full hover:bg-rr-navy transition-colors flex items-center justify-center gap-2 disabled:opacity-60 w-fit">
          {adding ? <Loader2 size={16} className="animate-spin" /> : <><UserPlus size={15} /> Add Team Member</>}
        </button>
      </form>

      {team.length === 0 && <p data-testid="broker-team-empty-state" className="text-rr-muted text-sm">No team members yet.</p>}
      <div className="space-y-2">
        {team.map((m) => (
          <div key={m.id} data-testid={`broker-team-row-${m.id}`} className="bg-white border border-rr-line rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="font-heading font-bold text-rr-navy text-sm">{m.member_name}</p>
              <p className="text-xs text-rr-muted">{m.member_email} · {m.role}</p>
            </div>
            <button onClick={() => removeMember(m.id)} data-testid={`broker-team-remove-button-${m.id}`} className="text-rr-red hover:opacity-70">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
